import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'

import { minify as minifyCode } from 'terser'

import { useNotifications } from 'app/application/application'
import { combineClassnames } from 'app/common/presentation'
import { CopyIcon } from 'app/common/presentation/icons'

import { Button } from 'components'

import styles from './Code.module.scss'

interface CodeProps {
  className?: string
  code: string
  language?: string
  minify?: boolean
  showCopy?: boolean
  what: string
}

export interface CodeFunctions {
  copyCode: () => void
}

const Code = forwardRef<CodeFunctions, CodeProps>(
  (
    { className, code: incomingCode, showCopy = false, language = 'json', minify = false, what },
    ref,
  ) => {
    const { createNotification } = useNotifications()
    const { t } = useTranslation('common')

    const [code, setCode] = useState(minify ? '' : incomingCode)

    useEffect(() => {
      if (minify) {
        const scriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi
        const match = scriptRegex.exec(incomingCode)

        if (!match || match.length < 2) {
          void minifyCode(incomingCode).then((output) => {
            setCode(output.code ?? '')
          })
        } else {
          // enclosed in script tags
          const originalTag = /<script\b[^>]*>/i.exec(match[0])?.[0] ?? '<script>'
          const scriptContent = match[1]

          void minifyCode(scriptContent).then((output) => {
            setCode(`${originalTag}\n${output.code ?? ''}\n</script>`)
          })
        }
      } else {
        setCode(incomingCode)
      }
    }, [incomingCode, minify])

    const handleCopySuccess = () =>
      createNotification({
        message: t('copy.copySuccessful.message', { what }),
        type: 'success',
        title: t('copy.copySuccessful.title'),
      })

    const copyCode = () => {
      void navigator.clipboard.writeText(code).then(handleCopySuccess)
    }

    useImperativeHandle(ref, () => ({
      copyCode,
    }))

    return (
      <div className={combineClassnames(styles.container, className)}>
        <pre className={styles.lineNumbers}>
          <code>{code.split('\n').map((_, index) => `${index + 1}\n`)}</code>
        </pre>

        <SyntaxHighlighter
          className={combineClassnames(styles.code, minify ? styles.minified : undefined)}
          codeTagProps={{ style: undefined }}
          data-testid="code"
          language={language}
        >
          {code}
        </SyntaxHighlighter>
        {showCopy && (
          <Button
            data-testid="copy"
            leftIcon={<CopyIcon />}
            onClick={copyCode}
            small
            variant="secondary"
          >
            {t('copy.title')}
          </Button>
        )}
      </div>
    )
  },
)

Code.displayName = 'Code'

export default Code
